import React, { CSSProperties, useMemo } from "react";
import {
  cn,
  containsHtmlTags,
  isVoidElement,
  manaSymbolsPairs,
  planesWalkerPairs,
} from "utils/presentation";
import reactStringReplace from "react-string-replace";
import {
  BRACKET_TOKEN,
  collapseSourceBreaks,
  type RulesTokenAppearance,
} from "modules/cards/presentation/cardRulesText";
import parse, {
  DOMNode,
  domToReact,
  Element,
  HTMLReactParserOptions,
} from "html-react-parser";

interface ManaSymbolsProps {
  costPrinted: string;
  extractClassesFunc: (costPrinted: string) => string[];
  showColorless?: boolean;
  style?: CSSProperties;
  className?: string;
  noMsCost?: boolean;
}

export const ManaFontManaSymbols = ({
  costPrinted = "",
  extractClassesFunc,
  style,
  className = "",
  noMsCost = false,
}: ManaSymbolsProps) => {
  const manaElements = useMemo(() => {
    if (!costPrinted) return [];

    const costs = extractClassesFunc(costPrinted);
    return costs.map((cost, idx) => {
      const cleanCost = cost.trim().toLowerCase();
      return (
        <i
          key={`mana-${cleanCost}-${idx}`}
          className={`ms ms-${cleanCost} ${!noMsCost ? "ms-cost" : ""}`}
          style={style}
        />
      );
    });
  }, [costPrinted, extractClassesFunc, noMsCost, style]);

  if (!manaElements.length) return null;

  return (
    <div className={`flex shrink-0 gap-1 ${className}`}>{manaElements}</div>
  );
};

interface TypeSymbolsProps {
  type: string;
  className?: string;
}

export const ManaFontTypeSymbols = ({
  type,
  className = "",
}: TypeSymbolsProps) => {
  const typeElements = useMemo(() => {
    if (!type) return [];

    const types = type.includes(",")
      ? type.split(",").map((t) => t.trim())
      : [type.trim()];

    return types.map((typeStr, idx) => {
      const normalizedType =
        typeStr === "Kindred" ? "tribal" : typeStr.toLowerCase();
      if (typeStr === "Signature") return null;
      return (
        <i
          key={`type-${normalizedType}-${idx}`}
          className={`ms ms-${normalizedType} ms-shadow ms-fw`}
        />
      );
    });
  }, [type]);

  if (!typeElements.length) return null;

  return (
    <div className={cn(`flex shrink-0 flex-row gap-1`, className)}>
      {typeElements}
    </div>
  );
};

const COLOR_NAME_TO_MANA_CLASS: Record<string, string> = {
  White: "w",
  Blue: "u",
  Black: "b",
  Red: "r",
  Green: "g",
  Colorless: "c",
  W: "w",
  U: "u",
  B: "b",
  R: "r",
  G: "g",
  C: "c",
};

export function ManaColorIcon({
  color,
  className,
}: {
  color: string;
  className?: string;
}) {
  const cls = COLOR_NAME_TO_MANA_CLASS[color];
  if (!cls) return null;
  return <i className={cn(`ms ms-${cls} ms-shadow`, className)} />;
}

interface ReplacedSymbolsOracleTextProps {
  oracleText: string;
  style?: CSSProperties;
  className?: string;
  replaceSymbols?: boolean;
  // The game supplies printed token colors; unresolved tokens and prose
  // still go through symbol replacement.
  resolveTokenAppearance?: (
    bracketed: string
  ) => RulesTokenAppearance | undefined;
}

export const ReplacedSymbolsOracleText = ({
  oracleText = "",
  style,
  className = "",
  replaceSymbols = true,
  resolveTokenAppearance,
}: ReplacedSymbolsOracleTextProps) => {
  const processedText = useMemo(() => {
    if (!oracleText) return null;
    const source = collapseSourceBreaks(oracleText);

    let symbolKeyCounter = 0;
    let elementKeyCounter = 0;

    // Helper function to process text nodes for symbol replacement
    const replaceSymbolsInText = (text: string): React.ReactNode[] => {
      if (!replaceSymbols) return [text];

      let result: React.ReactNode[] = [text];

      // Process mana symbols first
      for (const [symbol, cssClass] of Object.entries(manaSymbolsPairs)) {
        result = reactStringReplace(result, symbol, () => (
          <i
            key={`mana-${symbolKeyCounter++}`}
            className={`ms ms-${cssClass.toLowerCase()} ms-cost ms-shadow`}
            style={style}
          />
        ));
      }

      // Process planeswalker loyalty symbols
      for (const [symbol, cssClass] of Object.entries(planesWalkerPairs)) {
        const loyaltyDirection = symbol.includes("+")
          ? "ms-loyalty-up"
          : "ms-loyalty-down";
        result = reactStringReplace(result, symbol, () => (
          <i
            key={`loyalty-${symbolKeyCounter++}`}
            className={cn(
              `ms-2x ms ms-loyalty-${cssClass.toLowerCase()} before:text-primary after:!text-base-100`,
              loyaltyDirection
            )}
          />
        ));
      }

      return result;
    };

    // Badge resolvable [Bracket Tokens] first; everything else (including
    // unresolved tokens) still goes through symbol replacement.
    // The clip-path slants the badge sides to match the parallelogram keyword
    // boxes printed on Riftbound cards.
    const badgeAndReplaceText = (text: string): React.ReactNode[] => {
      if (!resolveTokenAppearance) return replaceSymbolsInText(text);
      return text.split(BRACKET_TOKEN).flatMap((part): React.ReactNode[] => {
        if (part.startsWith("[") && part.endsWith("]")) {
          const appearance = resolveTokenAppearance(part);
          if (appearance) {
            return [
              <span
                key={`token-${symbolKeyCounter++}`}
                style={{
                  ...appearance.style,
                  clipPath:
                    "polygon(0.35em 0, 100% 0, calc(100% - 0.35em) 100%, 0 100%)",
                  WebkitClipPath:
                    "polygon(0.35em 0, 100% 0, calc(100% - 0.35em) 100%, 0 100%)",
                  fontFamily:
                    "'Arial Narrow', 'Avenir Next Condensed', sans-serif",
                  // The condensed capitals sit high in their line box. A little
                  // more space above them keeps the printed label optically centered.
                  display: "inline-block",
                  height: "auto",
                  minHeight: 0,
                  lineHeight: 1,
                  padding: "0.22em 0.65em 0.12em",
                }}
                className={cn(
                  "badge badge-sm mr-0.5 rounded-none border-0 align-baseline font-bold uppercase italic shadow-none",
                  appearance.className
                )}
              >
                {part.slice(1, -1)}
              </span>,
            ];
          }
        }
        return replaceSymbolsInText(part);
      });
    };

    // If no HTML tags, use the original symbol replacement approach
    if (!containsHtmlTags(source)) {
      return badgeAndReplaceText(source);
    }

    // HTML parser options to process text nodes for symbol replacement
    const parserOptions: HTMLReactParserOptions = {
      replace: (domNode) => {
        // Process text nodes to replace symbols
        if (domNode.type === "text") {
          const replaced = badgeAndReplaceText(domNode.data);
          return <>{replaced}</>;
        }

        // For element nodes, recursively process children
        if (domNode.type === "tag") {
          const element = domNode as Element;

          // Void elements cannot have children
          if (isVoidElement(element.name)) {
            return React.createElement(element.name, {
              key: `elem-${elementKeyCounter++}`,
            });
          }

          // Non-void elements can have children
          const children = element.children
            ? domToReact(element.children as DOMNode[], parserOptions)
            : undefined;

          return React.createElement(
            element.name,
            { key: `elem-${elementKeyCounter++}` },
            children
          );
        }
      },
    };

    // Parse HTML and replace symbols in text nodes
    return parse(source, parserOptions);
  }, [oracleText, replaceSymbols, style, resolveTokenAppearance]);

  if (!processedText) return null;

  return (
    <div className="shrink-0">
      <p
        className={cn(
          `text-base-content whitespace-pre-line text-base ${className}`
        )}
      >
        {processedText}
      </p>
    </div>
  );
};
